/**
 * Änderungsvorschläge in das gespeicherte Mail-HTML übernehmen.
 *
 * Drei Wege, in dieser Reihenfolge:
 *  1. genau — nur die geänderten Textstücke im Quelltext ersetzen (Text
 *     maskiert: & < > und geschützte Leerzeichen). Alles andere bleibt
 *     Zeichen für Zeichen.
 *  2. block — der Inhalt des Blocks wird neu geschrieben (gleiche Elemente,
 *     neue Texte), wenn der Quelltext die Textstücke anders aufteilt als der
 *     Browser.
 *  3. html — Struktur oder Gestaltung geändert (Umbruch, fett, Farbe, Link …):
 *     bereinigtes HTML des Blocks einsetzen.
 * Nach jeder Änderung wird neu gelesen und geprüft; passt das Ergebnis nicht,
 * bleibt der Quelltext unverändert. Geschrieben wird nur in echte Textblöcke
 * (nie in style, title, head …), und nur, wenn danach genau nachher_text im
 * Block steht — so kommt beim Annehmen in die Mail, was die Karte zeigt.
 *
 * Vierter Weg: Ein Abschnitt (Absatz, Kasten, Zeile …) wird samt Gestaltung
 * entfernt (`entfernen`). Das Stück [start, ende) des Elements fliegt aus dem
 * Quelltext, ganze Zeilen samt Einrückung. Danach wird neu gelesen: außerhalb
 * des Abschnitts muss alles unverändert sein.
 */
import type { Aenderung, BlockSchluessel } from './bloecke';
import {
  OHNE_TEXT, STRUKTUR, dekodieren, domBlockText, domTextKnoten, normalisieren, quellBloecke, quellBlockText, quellTextKnoten,
  quelltextLesen,
  type Quelltext,
} from './quelltext';
import {
  ABMELDE, ABSCHNITT_BILDER_MAX, ABSCHNITT_BILD_MAX, ABSCHNITT_TAGS, ABSCHNITT_TEXT_MAX, VERSTECKT_STIL, bedingungenAusgeglichen,
  neuerLinkErlaubt,
} from './regeln';

export type AnwendeWeg = 'genau' | 'block' | 'html' | 'entfernt';

export interface AnwendeErgebnis {
  html: string;
  /** Je bearbeiteter Änderung (Index in `aenderungen`). grund: nicht_gefunden,
   *  mehrdeutig, schon_angewendet, abweichend, pruefung, ungueltig. */
  ergebnisse: { index: number; ok: boolean; weg: AnwendeWeg | null; grund?: string }[];
}

// ── Maskieren und Bereinigen ────────────────────────────────────────────────

/** Text für den Quelltext: & < > maskiert, geschütztes Leerzeichen als &nbsp;. */
export function kodiereText(text: string): string {
  let out = '';
  for (const c of text) {
    switch (c) {
      case '&': out += '&amp;'; break;
      case '<': out += '&lt;'; break;
      case '>': out += '&gt;'; break;
      case ' ': out += '&nbsp;'; break;
      case '\r': out += '&#13;'; break;
      case '\u0000': break;
      default: out += c;
    }
  }
  return out;
}

const HTML_NS = 'http://www.w3.org/1999/xhtml';
const ERLAUBT = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'br', 'span', 'a', 'font', 'small', 'sup', 'sub', 'mark', 'img']);
/** Fliegen samt Inhalt raus. */
const ENTFERNEN = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'textarea', 'select', 'input', 'button',
  'svg', 'math', 'title', 'head', 'meta', 'link', 'base', 'frame', 'frameset', 'applet', 'noembed', 'noframes',
  'xmp', 'plaintext', 'audio', 'video', 'canvas', 'picture', 'source', 'track', 'param', 'map', 'area', 'portal',
]);
/** Beim Auspacken bleibt ein Zeilenumbruch, wo vorher ein Block war (z. B. Enter → <div>). */
const BLOCKARTIG = new Set([...STRUKTUR, 'address', 'details', 'summary', 'dialog', 'fieldset', 'legend', 'hgroup', 'menu', 'dir', 'search', 'caption']);
const ATTRIBUTE = new Set(['style', 'class', 'href', 'target', 'rel', 'title', 'color', 'face', 'size', 'align', 'dir', 'lang']);
const BILD_ATTRIBUTE = new Set(['src', 'alt', 'width', 'height', 'border']);

function linkOk(wert: string): boolean {
  const w = wert.trim();
  for (let i = 0; i < w.length; i++) if (w.charCodeAt(i) < 32 || w.charCodeAt(i) === 127) return false;
  return /^(https?:\/\/|mailto:|tel:|#)/i.test(w) || /^\{\{[^{}<>"'`]*\}\}$/.test(w);
}

function stilOk(wert: string): boolean {
  return !/expression|javascript|vbscript|url\s*\(|@import|behavior|binding|\\|<|>|\/\*/i.test(wert);
}

function attributOk(tag: string, name: string, wert: string): boolean {
  if (!ATTRIBUTE.has(name) && !(tag === 'img' && BILD_ATTRIBUTE.has(name))) return false;
  switch (name) {
    case 'href': return linkOk(wert);
    case 'src': return /^https:\/\/[^\s"'<>`]+$/i.test(wert.trim());
    case 'style': return stilOk(wert);
    case 'target': return /^_(blank|self)$/i.test(wert);
    case 'rel': return /^[\w -]{1,60}$/.test(wert);
    case 'dir': return /^(ltr|rtl|auto)$/i.test(wert);
    case 'lang': return /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/i.test(wert);
    case 'color': return /^#?[0-9a-z]{1,20}$/i.test(wert);
    case 'face': return /^[\w ,'"-]{1,100}$/.test(wert);
    case 'size': return /^[+-]?\d{1,2}$/.test(wert);
    case 'align': return /^(left|right|center|justify|top|middle|bottom)$/i.test(wert);
    case 'width': case 'height': return /^\d{1,4}(%|px)?$/.test(wert);
    case 'border': return /^\d{1,2}$/.test(wert);
    default: return wert.length <= 300; // title, alt, class
  }
}

function sinnvoll(k: Node | null, richtung: 'vor' | 'nach'): boolean {
  while (k && k.nodeType === 3 && !/\S/.test((k as Text).data)) k = richtung === 'vor' ? k.previousSibling : k.nextSibling;
  if (!k) return false;
  if (k.nodeType === 3) return true;
  return k.nodeType === 1 && (k as Element).localName !== 'br';
}

function auspacken(e: Element): void {
  const eltern = e.parentNode!;
  if (BLOCKARTIG.has(e.localName) && e.firstChild) {
    if (sinnvoll(e.previousSibling, 'vor')) eltern.insertBefore(e.ownerDocument.createElement('br'), e);
    if (sinnvoll(e.nextSibling, 'nach')) eltern.insertBefore(e.ownerDocument.createElement('br'), e.nextSibling);
  }
  while (e.firstChild) eltern.insertBefore(e.firstChild, e);
  e.remove();
}

function hatKlasse(e: Element, k: string): boolean {
  return (' ' + (e.getAttribute('class') ?? '') + ' ').includes(' ' + k + ' ');
}

function saeubern(wurzel: Node, ohneLinks: boolean): void {
  for (const k of Array.from(wurzel.childNodes)) {
    if (k.nodeType === 3) continue;
    if (k.nodeType !== 1) { k.parentNode?.removeChild(k); continue; } // Kommentare (auch MSO-Bedingungen)
    const e = k as Element;
    const tag = e.localName;
    if (e.namespaceURI !== HTML_NS || ENTFERNEN.has(tag) || hatKlasse(e, 'za-weg')) { e.remove(); continue; }
    saeubern(e, ohneLinks);
    if (!ERLAUBT.has(tag) || (tag === 'a' && ohneLinks) || hatKlasse(e, 'za-neu') || hatKlasse(e, 'za-zitat')) {
      auspacken(e);
      continue;
    }
    for (const a of Array.from(e.attributes)) {
      if (a.name === 'class') {
        const klassen = a.value.split(/\s+/).filter((c) => c && !c.startsWith('za-') && /^[\w-]{1,60}$/.test(c));
        if (klassen.length) e.setAttribute('class', klassen.join(' ')); else e.removeAttribute('class');
      } else if (!attributOk(tag, a.name, a.value)) e.removeAttribute(a.name);
    }
    if (tag === 'img' && !e.hasAttribute('src')) e.remove();
    else if ((tag === 'span' || tag === 'font') && !e.attributes.length) auspacken(e);
  }
}

/**
 * Bereinigt den Inhalt eines Blocks (Positivliste): erlaubt sind b, strong, i,
 * em, u, s, br, span, a, font, small, sup, sub, mark und img (nur https-Bilder);
 * andere Elemente werden ausgepackt, script/style/iframe/object/embed/svg …
 * samt Inhalt entfernt, Kommentare ebenso. Attribute nur style, class, href,
 * target, rel, title, color, face, size, align, dir, lang (Bild: src, alt,
 * width, height, border); href nur http(s):, mailto:, tel:, # und {{…}};
 * style ohne expression(/javascript:/url(. on*-, contenteditable- und
 * data-Attribute fallen damit weg.
 */
export function bereinigen(innerHtml: string, optionen: { ohneLinks?: boolean } = {}): string {
  const tpl = document.createElement('template');
  tpl.innerHTML = innerHtml;
  saeubern(tpl.content, !!optionen.ohneLinks);
  return tpl.innerHTML;
}

// ── Anwenden ────────────────────────────────────────────────────────────────

function gueltig(a: Aenderung): boolean {
  const istText = (x: unknown) => typeof x === 'string';
  if (a && typeof a === 'object' && a.entfernen !== undefined && a.entfernen !== null) return entfernungGueltig(a);
  return !!a && typeof a === 'object' && !!a.block && istText(a.block.tag) && /^[a-z][a-z0-9:-]*$/.test(a.block.tag)
    && Number.isInteger(a.block.nr) && a.block.nr >= 0 && istText(a.vorher_text) && istText(a.nachher_text)
    && Array.isArray(a.vorher_knoten) && a.vorher_knoten.every(istText)
    && (a.nachher_knoten === null || a.nachher_knoten === undefined || (Array.isArray(a.nachher_knoten) && a.nachher_knoten.every(istText)))
    && (a.nachher_html === null || a.nachher_html === undefined || istText(a.nachher_html))
    && (!!a.nachher_knoten || istText(a.nachher_html));
}

export type QuellFund = { i: number } | { grund: string };

/**
 * Textblock im Quelltext: gleiche Nummer und gleicher Text, sonst eindeutig
 * über den Text. Nur echte Textblöcke (quellBloecke) — ein Schlüssel, der auf
 * style, title, head oder ein anderes Element zeigt, findet nichts.
 */
export function quellBlockFinden(q: Quelltext, schluessel: BlockSchluessel, vorherText: string, nachherText?: string): QuellFund {
  const bloecke = new Set(quellBloecke(q));
  const liste = q.nachTag.get(schluessel.tag) ?? [];
  const genau = liste[schluessel.nr];
  if (genau !== undefined) {
    const t = quellBlockText(q, genau);
    if (t === vorherText && bloecke.has(genau)) return { i: genau };
    // steht an der Stelle schon der neue Text, nicht anderswo noch einmal anwenden
    if (nachherText !== undefined && nachherText !== vorherText && t === nachherText && !q.elemente[genau].ohneText) {
      return { grund: 'schon_angewendet' };
    }
  }
  // Textblöcke sind maximal — verschachtelt kommen sie nicht vor
  const treffer = liste.filter((i) => bloecke.has(i) && quellBlockText(q, i) === vorherText);
  if (treffer.length === 1) return { i: treffer[0] };
  return { grund: treffer.length > 1 ? 'mehrdeutig' : 'nicht_gefunden' };
}

/** Alles außerhalb von Element i (sein Inhalt übersprungen): Tags mit Tiefe, Texte, Kommentare. */
function aussenherum(q: Quelltext, i: number): string {
  const teile: string[] = [];
  const laufen = (e: number, tiefe: number) => {
    for (const k of q.kinder[e + 1]) {
      if (k.art === 'text') teile.push(`${tiefe}#${q.texte[k.i].text}`);
      else if (k.art === 'kommentar') teile.push(`${tiefe}!`);
      else {
        teile.push(`${tiefe}<${q.elemente[k.i].tag}`);
        if (k.i !== i) laufen(k.i, tiefe + 1);
      }
    }
  };
  laufen(-1, 0);
  return teile.join('\u0001');
}

function gleich(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, k) => x === b[k]);
}

function ersetzen(html: string, stellen: [number, number, string][]): string {
  let out = html;
  for (const [a, b, neu] of [...stellen].sort((x, y) => y[0] - x[0])) out = out.slice(0, a) + neu + out.slice(b);
  return out;
}

function inLink(q: Quelltext, i: number): boolean {
  for (let p = i; p >= 0; p = q.elemente[p].eltern) if (q.elemente[p].tag === 'a') return true;
  return false;
}

function fragment(innerHtml: string): DocumentFragment {
  const tpl = document.createElement('template');
  tpl.innerHTML = innerHtml;
  return tpl.content;
}

/** Versteckt Text (oder schiebt ihn weg), ohne dass er im Textvergleich fehlt. */
const VERSTECKT = /display\s*:\s*none|visibility|opacity|mso-hide|position|text-indent|clip|transform|(^|[;\s])(font-size|max-height|max-width|line-height|height|width)\s*:\s*0(?![.\d])|(^|[;\s])color\s*:\s*transparent/i;

/** Linkziele, Bildquellen und versteckende Stile in einem Stück HTML. */
function heikles(f: DocumentFragment): Set<string> {
  const out = new Set<string>();
  f.querySelectorAll('[href],[src],[style]').forEach((e) => {
    const href = e.getAttribute('href');
    const src = e.getAttribute('src');
    const stil = e.getAttribute('style');
    if (href !== null) out.add(`h${href.trim()}`);
    if (src !== null) out.add(`s${src.trim()}`);
    if (stil !== null && VERSTECKT.test(stil)) out.add(`c${stil.replace(/\s+/g, '').toLowerCase()}`);
  });
  return out;
}

// ── Abschnitte entfernen ────────────────────────────────────────────────────

function entfernungGueltig(a: Aenderung): boolean {
  const e = a.entfernen;
  const istText = (x: unknown) => typeof x === 'string';
  return !!e && typeof e === 'object' && istText(e.art) && Array.isArray(e.bilder) && e.bilder.length <= ABSCHNITT_BILDER_MAX
    && e.bilder.every((b) => istText(b) && b.length <= ABSCHNITT_BILD_MAX)
    && !!a.block && istText(a.block.tag) && ABSCHNITT_TAGS.has(a.block.tag)
    && Number.isInteger(a.block.nr) && a.block.nr >= 0
    && istText(a.vorher_text) && a.vorher_text.length <= ABSCHNITT_TEXT_MAX && a.nachher_text === '';
}

/** Wert eines Attributs aus dem Start-Tag (der erste gilt, Entitäten aufgelöst). */
function attributWert(startTag: string, name: string): string | null {
  const m = /^<[^\s/>]+/.exec(startTag);
  if (!m) return null;
  const re = /([^\s"'<>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  re.lastIndex = m[0].length;
  for (let x = re.exec(startTag); x; x = re.exec(startTag)) {
    if (x[1].toLowerCase() === name) return dekodieren(x[2] ?? x[3] ?? x[4] ?? '');
  }
  return null;
}

function startTag(q: Quelltext, i: number): string {
  const e = q.elemente[i];
  return q.html.slice(e.start, e.innenStart);
}

/** Bilder (src) unter Element i — dieselbe Zählung wie abschnittBilder() im Browser. */
export function quellBilder(q: Quelltext, i: number): string[] {
  const out: string[] = [];
  const laufen = (e: number) => {
    for (const k of q.kinder[e + 1]) {
      if (k.art !== 'element') continue;
      const el = q.elemente[k.i];
      if (OHNE_TEXT.has(el.tag)) continue;
      if (el.tag === 'img' && el.ns === 'html') {
        const s = attributWert(startTag(q, k.i), 'src');
        if (s) out.push(s);
      }
      laufen(k.i);
    }
  };
  laufen(i);
  return out;
}

function quellVersteckt(q: Quelltext, i: number): boolean {
  for (let p = i; p >= 0; p = q.elemente[p].eltern) {
    const stil = attributWert(startTag(q, p), 'style');
    if (stil && VERSTECKT_STIL.test(stil)) return true;
  }
  return false;
}

function istUnter(q: Quelltext, j: number, i: number): boolean {
  for (let p = j; p >= 0; p = q.elemente[p].eltern) if (p === i) return true;
  return false;
}

function gleicheBilder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, k) => x === b[k]);
}

/**
 * Das Element im Quelltext, das zu einer Entfernung gehört: an der Stelle mit
 * gleichem Text und gleichen Bildern, sonst das einzige Element dieses Tags
 * damit. Prüft auch, ob es sich sauber herausnehmen lässt.
 */
export function quellAbschnittFinden(q: Quelltext, a: Aenderung): QuellFund {
  if (!a.entfernen || !a.block) return { grund: 'ungueltig' };
  const text = normalisieren(a.vorher_text);
  const bilder = a.entfernen.bilder;
  const liste = q.nachTag.get(a.block.tag) ?? [];
  const passt = (i: number) => {
    const e = q.elemente[i];
    return e.ns === 'html' && !e.ergaenzt && !e.ohneText && e.nr >= 0 && ABSCHNITT_TAGS.has(e.tag)
      && quellBlockText(q, i) === text && gleicheBilder(quellBilder(q, i), bilder);
  };
  let i: number | undefined = liste[a.block.nr];
  if (i === undefined || !passt(i)) {
    const treffer = liste.filter(passt);
    if (treffer.length !== 1) return { grund: treffer.length > 1 ? 'mehrdeutig' : 'nicht_gefunden' };
    i = treffer[0];
  }
  const e = q.elemente[i];
  const stueck = q.html.slice(e.start, e.ende);
  if (ABMELDE.test(stueck)) return { grund: 'abmelden' };
  if (!bedingungenAusgeglichen(stueck)) return { grund: 'bedingung' };
  // Es muss sichtbarer Text bleiben
  const bleibt = quellBloecke(q).some((b) => !istUnter(q, b, i) && !quellVersteckt(q, b));
  if (!bleibt) return { grund: 'alles' };
  return { i };
}

/** Lässt sich diese Entfernung im Quelltext sauber herausnehmen (ohne es zu tun)? */
export function quellAbschnittPasst(q: Quelltext, a: Aenderung): boolean {
  return 'i' in quellAbschnittFinden(q, a);
}

/**
 * Alles außerhalb von `aus` (das Element samt Inhalt fehlt) als Folge: Tags
 * mit Tiefe, Texte, Kommentare. Nebeneinander liegende Texte zählen als einer,
 * reiner Leerraum gar nicht — so ist das Ergebnis nach dem Herausnehmen
 * vergleichbar. `aus = -2`: nichts wird ausgelassen.
 */
function signaturOhne(q: Quelltext, aus: number, mitKommentar = false): string {
  const teile: string[] = [];
  const laufen = (e: number, tiefe: number) => {
    let text = '';
    const leeren = () => {
      if (/\S/.test(text)) teile.push(`${tiefe}#${text}`);
      text = '';
    };
    for (const k of q.kinder[e + 1]) {
      if (k.art === 'text') { text += q.texte[k.i].text; continue; }
      if (k.art === 'kommentar') { leeren(); teile.push(`${tiefe}!`); continue; }
      if (k.i === aus) {
        // der Kommentar unmittelbar davor geht mit
        if (mitKommentar && !/\S/.test(text) && teile[teile.length - 1] === `${tiefe}!`) teile.pop();
        continue;
      }
      leeren();
      teile.push(`${tiefe}<${q.elemente[k.i].tag}`);
      laufen(k.i, tiefe + 1);
    }
    leeren();
  };
  laufen(-1, 0);
  return teile.join('\u0001');
}

/**
 * Beginn des gewöhnlichen Kommentars (`<!-- HERO -->`), der allein in der Zeile
 * direkt über der Zeile ab `zeilenStart` steht; sonst -1. Bedingte Kommentare
 * (`<!--[if mso]>`, `<![endif]-->`) bleiben stehen.
 */
function kommentarDavor(html: string, zeilenStart: number): number {
  let p = zeilenStart;
  if (p === 0) return -1;
  p -= html[p - 1] === '\n' && html[p - 2] === '\r' ? 2 : 1;
  while (p > 0 && (html[p - 1] === ' ' || html[p - 1] === '\t')) p--;
  if (html.slice(p - 3, p) !== '-->') return -1;
  const auf = html.lastIndexOf('<!--', p - 3);
  if (auf < 0 || html.indexOf('-->', auf + 4) !== p - 3) return -1;
  const inhalt = html.slice(auf + 4, p - 3);
  if (/^\s*(\[if\b|<!\s*\[endif|\[endif)/i.test(inhalt) || /<!\s*\[endif\]\s*$/i.test(inhalt)) return -1;
  let z = auf;
  while (z > 0 && (html[z - 1] === ' ' || html[z - 1] === '\t')) z--;
  return z === 0 || html[z - 1] === '\n' ? z : -1;
}

/**
 * Bereich im Quelltext: Steht das Element allein auf seinen Zeilen, gehen die
 * Zeilen samt Einrückung mit — und ein Kommentar allein in der Zeile davor
 * (`<!-- HERO -->`), der ihm als Überschrift dient. Sonst genau das Element.
 */
function entfernBereich(html: string, start: number, ende: number): { von: number; bis: number; mitKommentar: boolean } {
  const genau = { von: start, bis: ende, mitKommentar: false };
  let v = start;
  while (v > 0 && (html[v - 1] === ' ' || html[v - 1] === '\t')) v--;
  if (v !== 0 && html[v - 1] !== '\n') return genau;
  let b = ende;
  while (b < html.length && (html[b] === ' ' || html[b] === '\t')) b++;
  if (html[b] === '\r' && html[b + 1] === '\n') b += 2;
  else if (html[b] === '\n') b += 1;
  else if (b < html.length) return genau;
  const k = kommentarDavor(html, v);
  return { von: k >= 0 ? k : v, bis: b, mitKommentar: k >= 0 };
}

function entfernenAnwenden(html: string, q: Quelltext, a: Aenderung): Einzel {
  const fund = quellAbschnittFinden(q, a);
  if ('grund' in fund) return { ok: false, weg: null, grund: fund.grund };
  const el = q.elemente[fund.i];
  const { von, bis, mitKommentar } = entfernBereich(html, el.start, el.ende);
  const neu = html.slice(0, von) + html.slice(bis);
  const q2 = quelltextLesen(neu, { quirks: q.quirks });
  if (signaturOhne(q, fund.i, mitKommentar) !== signaturOhne(q2, -2)) return { ok: false, weg: 'entfernt', grund: 'pruefung' };
  return { ok: true, weg: 'entfernt', html: neu, q: q2 };
}

interface Einzel { ok: boolean; weg: AnwendeWeg | null; grund?: string; html?: string; q?: Quelltext }

function einzelnAnwenden(html: string, q: Quelltext, a: Aenderung): Einzel {
  if (a.entfernen) return entfernenAnwenden(html, q, a);
  const vorherText = normalisieren(a.vorher_text);
  const nachherText = normalisieren(a.nachher_text);
  const fund = quellBlockFinden(q, a.block, vorherText, nachherText);
  if ('grund' in fund) return { ok: false, weg: null, grund: fund.grund };
  const i = fund.i;
  const el = q.elemente[i];
  const tag = el.tag;
  const nr = el.nr;
  const vorher = a.vorher_knoten;
  // Die neuen Textstücke müssen zusammen den gezeigten Text ergeben (<br> als Umbruch)
  const nachher = a.nachher_knoten && a.nachher_knoten.length === vorher.length
    && quellBlockText(q, i, a.nachher_knoten) === nachherText ? a.nachher_knoten : null;
  const soll = nachher ? nachher.filter((x) => x !== '') : null;

  // Prüfen: neu lesen — Block an gleicher Stelle mit den erwarteten Texten,
  // im Block steht genau nachher_text, außerhalb liest sich alles wie vorher
  let vorherAussen: string | null = null;
  const pruefen = (neu: string, weg: AnwendeWeg): Einzel => {
    const q2 = quelltextLesen(neu, { quirks: q.quirks });
    const j = q2.nachTag.get(tag)?.[nr];
    if (j === undefined) return { ok: false, weg, grund: 'pruefung' };
    vorherAussen ??= aussenherum(q, i);
    if (aussenherum(q2, j) !== vorherAussen) return { ok: false, weg, grund: 'pruefung' };
    const ok = quellBlockText(q2, j) === nachherText
      && (weg === 'html' || gleich(quellTextKnoten(q2, j).map((t) => q2.texte[t].text), soll!));
    return ok ? { ok: true, weg, html: neu, q: q2 } : { ok: false, weg, grund: 'pruefung' };
  };

  if (nachher) {
    // 1. genau
    const tks = quellTextKnoten(q, i);
    if (gleich(tks.map((t) => q.texte[t].text), vorher)) {
      const stellen: [number, number, string][] = [];
      let geht = true;
      tks.forEach((t, k) => {
        if (nachher[k] === vorher[k]) return;
        const qt = q.texte[t];
        if (qt.teile.length !== 2) { geht = false; return; }
        // Zeilenenden des Quelltexts beibehalten (CRLF bleibt CRLF)
        const neu = kodiereText(nachher[k]);
        stellen.push([qt.start, qt.ende, html.slice(qt.start, qt.ende).includes('\r\n') ? neu.replace(/\n/g, '\r\n') : neu]);
      });
      if (geht) {
        const r = pruefen(ersetzen(html, stellen), 'genau');
        if (r.ok) return r;
      }
    }
    // 2. block
    if (!el.ergaenzt) {
      const tpl = document.createElement('template');
      tpl.innerHTML = html.slice(el.innenStart, el.innenEnde);
      const knoten = domTextKnoten(tpl.content);
      if (gleich(knoten.map((t) => t.data), vorher)) {
        knoten.forEach((t, k) => { if (t.data !== nachher[k]) t.data = nachher[k]; });
        const r = pruefen(ersetzen(html, [[el.innenStart, el.innenEnde, tpl.innerHTML]]), 'block');
        if (r.ok) return r;
      }
    }
  }
  // 3. html — nur, wenn es den gezeigten Text ergibt und keine neuen Bilder
  // oder versteckenden Stile mitbringt; neue Links nur https/mailto (die Karte
  // zeigt ihre Adresse, bevor jemand annimmt)
  if (typeof a.nachher_html === 'string' && !el.ergaenzt) {
    const sauber = bereinigen(a.nachher_html, { ohneLinks: inLink(q, i) });
    const neu = fragment(sauber);
    if (domBlockText(neu) !== nachherText) return { ok: false, weg: null, grund: 'abweichend' };
    const erlaubt = heikles(fragment(html.slice(el.innenStart, el.innenEnde)));
    for (const x of heikles(neu)) {
      if (erlaubt.has(x) || (x.startsWith('h') && neuerLinkErlaubt(x.slice(1)))) continue;
      return { ok: false, weg: null, grund: 'abweichend' };
    }
    return pruefen(ersetzen(html, [[el.innenStart, el.innenEnde, sauber]]), 'html');
  }
  return { ok: false, weg: null, grund: 'abweichend' };
}

/**
 * Übernimmt Änderungen nacheinander in `html` (nur `nurIndizes`, wenn
 * angegeben). Was nicht passt, wird übersprungen und in `ergebnisse` mit
 * Grund gemeldet; der Rest des Quelltexts bleibt unverändert.
 */
export function aenderungenAnwenden(html: string, aenderungen: Aenderung[], nurIndizes?: number[]): AnwendeErgebnis {
  const nur = nurIndizes ? new Set(nurIndizes) : null;
  const ergebnisse: AnwendeErgebnis['ergebnisse'] = [];
  let aktuell = html;
  let q: Quelltext | null = null;
  aenderungen.forEach((a, index) => {
    if (nur && !nur.has(index)) return;
    if (!gueltig(a)) { ergebnisse.push({ index, ok: false, weg: null, grund: 'ungueltig' }); return; }
    q ??= quelltextLesen(aktuell);
    const r = einzelnAnwenden(aktuell, q, a);
    if (r.ok && r.html !== undefined && r.q) { aktuell = r.html; q = r.q; }
    ergebnisse.push(r.grund ? { index, ok: r.ok, weg: r.ok ? r.weg : null, grund: r.grund } : { index, ok: r.ok, weg: r.weg });
  });
  return { html: aktuell, ergebnisse };
}
